/* ── API versioning UI ─────────────────────────────────────────────────────
 *
 * Everything the operator and the customer need to see about API versions, in
 * one file that nothing else owns.
 *
 * WHY A SEPARATE FILE. Frontend updates arrive as `static.zip` from a design
 * lineage forked off an older commit, and are merged three-way. index.html and
 * portal.js are the two files that conflict every time. A file the export has
 * never heard of cannot conflict at all, so the merge surface for this feature
 * is reduced to a handful of one-line hooks — each listed in
 * `docs/ui-deltas/api-versioning.md` so it can be re-applied in minutes if an
 * overwrite drops it.
 *
 * All top-level names here are unique across the whole static bundle. The
 * scripts share one global scope, so a repeated name silently shadows one of
 * the two definitions depending on load order.
 *
 * Depends on: API (index.html), React globals.
 */

/* ── Shared vocabulary ─────────────────────────────────────────────────── */

const VERSION_LIFECYCLE_BADGE={PUBLISHED:'success',DEPRECATED:'warning',DRAFT:'gray',RETIRED:'error'};

const VERSION_STRATEGY_OPTIONS=[
  ['URI_PATH','URI path — /api/invoice-endpoints/v2/…'],
  ['HEADER','Request header — X-API-Version: v2'],
  ['QUERY_PARAM','Query parameter — ?api-version=v2'],
  ['ACCEPT_HEADER','Accept header — application/vnd.taxlab.v2+json'],
  ['NONE','None — every caller gets the default']
];

/** Short, unambiguous day. Deliberately not the global fmtDate: this file is
 *  meant to survive a merge that renames or drops helpers in index.html. */
function versionDay(iso){
  if(!iso)return null;
  try{
    const d=new Date(iso);
    if(isNaN(d.getTime()))return iso;
    return d.toISOString().slice(0,10);
  }catch(e){return iso;}
}

/** Days until a sunset, negative once it has passed. Null when there is none. */
function versionDaysToSunset(iso){
  if(!iso)return null;
  const t=Date.parse(iso);
  if(isNaN(t))return null;
  return Math.ceil((t-Date.now())/86400000);
}

function versionSunsetTone(iso){
  const d=versionDaysToSunset(iso);
  if(d===null)return null;
  if(d<0)return 'error';
  if(d<=60)return 'error';
  return 'warning';
}

/** The APISIX sync result, rendered the same way the Access tab renders it. */
function VersionSyncReport({sync}){
  if(!sync)return null;
  return(
    <div style={{marginTop:12,fontSize:12}}>
      {(sync.applied||[]).map((a,i)=><div key={'a'+i} style={{color:'var(--success,#10b981)'}}>✓ {a}</div>)}
      {(sync.skipped||[]).map((s,i)=><div key={'s'+i} style={{color:'var(--text3)'}}>– {s}</div>)}
      {(sync.errors||[]).map((e,i)=><div key={'e'+i} style={{color:'var(--error,#ef4444)'}}>✕ {e}</div>)}
    </div>
  );
}

/* ── Admin: the chip on an API Products card ───────────────────────────── */

/**
 * The version marker for a product card. Reads only fields the list endpoint
 * already returns, so dropping it degrades to nothing rather than breaking.
 *
 * "default" is the load-bearing word here: with two versions of one product in
 * the grid, it is the only thing that says which of them the unversioned paths
 * actually reach — and that is not derivable from the name or the semver.
 */
function ProductVersionChip({product}){
  const token=product.apiVersion;
  if(!token)return null;
  const tone=versionSunsetTone(product.sunsetAt);
  return(
    <React.Fragment>
      <span className="badge badge-gray" style={{fontSize:10,fontFamily:'monospace'}}>{token}</span>
      {product.defaultVersion&&(
        <span className="badge badge-info" style={{fontSize:10}} title="Serves the unversioned paths">
          default
        </span>
      )}
      {product.sunsetAt&&(
        <span className={'badge badge-'+(tone||'warning')} style={{fontSize:10}}
              title={'Sunset '+versionDay(product.sunsetAt)}>
          sunset {versionDay(product.sunsetAt)}
        </span>
      )}
    </React.Fragment>
  );
}

/* ── Admin: the Versions tab ───────────────────────────────────────────── */

/**
 * The family view — every version of this API side by side, and the four
 * operations that move traffic between them.
 *
 * The order of the actions on screen is the order an operator should perform
 * them: create a draft, publish it, promote it, then deprecate the old one.
 * Promote is styled as the consequential one because it is the only step an
 * existing unversioned caller can notice.
 */
function ProductVersionsTab({product,onChanged,onSelectProduct,showToast,confirm}){
  const [family,setFamily]=useState(null);
  const [versions,setVersions]=useState([]);
  const [policy,setPolicy]=useState(null);
  const [loading,setLoading]=useState(true);
  const [busy,setBusy]=useState('');
  const [sync,setSync]=useState(null);
  const [showCreate,setShowCreate]=useState(false);
  const [deprecating,setDeprecating]=useState(null);

  const reload=useCallback(async()=>{
    setLoading(true);
    try{
      const d=await API('GET','/api/admin/api-products/'+product.id+'/versions');
      setFamily(d.family||null);
      setVersions(Array.isArray(d.versions)?d.versions:[]);
      setPolicy(d.policy||null);
    }catch(e){showToast&&showToast('Could not load versions: '+e.message,'error');}
    finally{setLoading(false);}
  },[product.id,showToast]);

  useEffect(()=>{reload();},[reload]);

  const act=async(id,action,body,label)=>{
    setBusy(id+':'+action);setSync(null);
    try{
      const res=await API('POST','/api/admin/api-products/'+id+'/versions/'+action,body||{});
      setSync(res.sync||null);
      showToast&&showToast(label,'success');
      await reload();
      await onChanged();
    }catch(e){showToast&&showToast(e.message,'error');}
    finally{setBusy('');}
  };

  const create=async(form)=>{
    setBusy('create');
    try{
      const res=await API('POST','/api/admin/api-products/'+product.id+'/versions',form);
      setShowCreate(false);
      showToast&&showToast('Created '+(res.product&&res.product.apiVersion||'a new version')
                          +' as a draft — nothing is live yet','success');
      await reload();
      await onChanged();
      if(onSelectProduct&&res.product)onSelectProduct(res.product.id);
    }catch(e){showToast&&showToast(e.message,'error');}
    finally{setBusy('');}
  };

  const promote=async(row)=>{
    const ok=confirm
      ?await confirm('Point the unversioned paths at '+row.apiVersion+'?\n\n'
        +'Every caller who does not name a version — including integrations built before '
        +'versioning existed — starts reaching '+row.apiVersion+' on the next request. '
        +'Callers who pinned a version are unaffected. This is reversible.')
      :window.confirm('Make '+row.apiVersion+' the default?');
    if(!ok)return;
    await act(row.id,'promote',{},row.apiVersion+' is now the default');
  };

  const retire=async(row)=>{
    const warn=row.activeSubscriptions>0
      ?'\n\n'+row.activeSubscriptions+' subscription'+(row.activeSubscriptions===1?' is':'s are')
        +' still bound to it. They will start receiving 410.'
      :'';
    const ok=confirm
      ?await confirm('Retire '+row.apiVersion+'?\n\nIts routes stop serving and answer '
        +'410 Gone, naming the successor.'+warn)
      :window.confirm('Retire '+row.apiVersion+'?');
    if(!ok)return;

    setBusy(row.id+':retire');setSync(null);
    try{
      const res=await API('POST','/api/admin/api-products/'+row.id+'/versions/retire',{});
      setSync(res.sync||null);
      showToast&&showToast(row.apiVersion+' retired','success');
      await reload();await onChanged();
    }catch(e){
      // The server refuses a retire that would strand live subscribers. Offer the
      // override here rather than making the operator find the query parameter —
      // but only for that refusal. It never offers to override the default-version
      // guard, because that one is always avoidable by promoting first.
      if(/force=true/.test(e.message||'')){
        const force=confirm?await confirm(e.message+'\n\nRetire anyway?')
                           :window.confirm(e.message+'\n\nRetire anyway?');
        if(force){
          try{
            const res=await API('POST','/api/admin/api-products/'+row.id+'/versions/retire?force=true',{});
            setSync(res.sync||null);
            showToast&&showToast(row.apiVersion+' retired','success');
            await reload();await onChanged();
          }catch(e2){showToast&&showToast(e2.message,'error');}
        }
      }else{
        showToast&&showToast(e.message,'error');
      }
    }finally{setBusy('');}
  };

  if(loading)return <div className="card"><div className="card-body">Loading versions…</div></div>;

  const isOnly=versions.length<=1;

  return(
    <div>
      <div className="card" style={{marginBottom:16}}>
        <div className="card-body">
          <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:12,flexWrap:'wrap'}}>
            <div>
              <div style={{fontWeight:600,marginBottom:4}}>
                Versions of <span style={{fontFamily:'monospace'}}>{family||'this API'}</span>
              </div>
              <p style={{fontSize:12,color:'var(--text3)',margin:0,maxWidth:640}}>
                Each version is its own product record with its own subscribers, so publishing
                a new one cannot affect anybody already on an old one. Exactly one version is
                the <strong>default</strong> — the one that answers the unversioned paths.
              </p>
            </div>
            <button className="btn btn-primary btn-sm" onClick={()=>setShowCreate(true)} disabled={busy==='create'}>
              New version
            </button>
          </div>

          {isOnly&&(
            <p style={{fontSize:12,color:'var(--text3)',marginBottom:0,marginTop:12}}>
              This API has one version. Creating a second clones it into a draft — nothing
              goes live, and no existing caller is affected, until you publish and promote it.
            </p>
          )}
        </div>
      </div>

      {versions.length>0&&(
        <div className="card" style={{marginBottom:16}}>
          <div className="card-body" style={{overflowX:'auto'}}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
              <thead>
                <tr style={{textAlign:'left',color:'var(--text3)',fontSize:11,textTransform:'uppercase'}}>
                  <th style={{padding:'8px 6px'}}>Version</th>
                  <th style={{padding:'8px 6px'}}>Base path</th>
                  <th style={{padding:'8px 6px'}}>State</th>
                  <th style={{padding:'8px 6px'}}>Subscribers</th>
                  <th style={{padding:'8px 6px'}}>Sunset</th>
                  <th style={{padding:'8px 6px',textAlign:'right'}}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {versions.map(row=>{
                  const here=row.id===product.id;
                  const tone=versionSunsetTone(row.sunsetAt);
                  const draft=row.lifecycle==='DRAFT';
                  const retired=row.lifecycle==='RETIRED';
                  return(
                    <tr key={row.id} style={{borderTop:'1px solid var(--border)',
                                             background:here?'var(--surface2,transparent)':undefined}}>
                      <td style={{padding:'10px 6px'}}>
                        <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
                          <span style={{fontFamily:'monospace',fontWeight:600}}>{row.apiVersion}</span>
                          {row.default&&<span className="badge badge-info" style={{fontSize:9}}>default</span>}
                          {here&&<span style={{fontSize:10,color:'var(--text3)'}}>(open)</span>}
                        </div>
                        <div style={{fontSize:11,color:'var(--text3)'}}>{row.name} · v{row.version}</div>
                      </td>
                      <td style={{padding:'10px 6px',fontFamily:'monospace',fontSize:11}}>
                        {row.basePath||'—'}
                        {row.default&&policy&&policy.allowUnversioned&&(
                          <div style={{color:'var(--text3)'}}>+ the unversioned path</div>
                        )}
                      </td>
                      <td style={{padding:'10px 6px'}}>
                        <span className={'badge badge-'+(VERSION_LIFECYCLE_BADGE[row.lifecycle]||'gray')}
                              style={{fontSize:10}}>{row.lifecycle}</span>
                      </td>
                      <td style={{padding:'10px 6px'}}>{row.activeSubscriptions}</td>
                      <td style={{padding:'10px 6px',fontSize:11,color:tone?'var(--'+tone+',inherit)':'var(--text3)'}}>
                        {row.sunsetAt?versionDay(row.sunsetAt):'—'}
                      </td>
                      <td style={{padding:'10px 6px',textAlign:'right',whiteSpace:'nowrap'}}>
                        {!row.default&&!draft&&!retired&&(
                          <button className="btn btn-xs btn-primary" style={{marginLeft:6}}
                                  disabled={!!busy} onClick={()=>promote(row)}>
                            Make default
                          </button>
                        )}
                        {row.lifecycle==='PUBLISHED'&&(
                          <button className="btn btn-xs btn-secondary" style={{marginLeft:6}}
                                  disabled={!!busy} onClick={()=>setDeprecating(row)}>
                            Deprecate
                          </button>
                        )}
                        {row.lifecycle==='DEPRECATED'&&(
                          <button className="btn btn-xs btn-secondary" style={{marginLeft:6}}
                                  disabled={!!busy}
                                  onClick={()=>act(row.id,'undeprecate',{},row.apiVersion+' is published again')}>
                            Undeprecate
                          </button>
                        )}
                        {!retired&&(
                          <button className="btn btn-xs btn-danger" style={{marginLeft:6}}
                                  disabled={!!busy} onClick={()=>retire(row)}>
                            Retire
                          </button>
                        )}
                        {!here&&onSelectProduct&&(
                          <button className="btn btn-xs btn-ghost" style={{marginLeft:6}}
                                  onClick={()=>onSelectProduct(row.id)}>
                            Open
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <VersionSyncReport sync={sync}/>
            {sync&&(
              <p style={{fontSize:11,color:'var(--text3)',marginBottom:0}}>
                Promote, deprecate and retire push their own routes. Creating a version and
                changing the policy do not — use Sync to APISIX on the Access tab.
              </p>
            )}
          </div>
        </div>
      )}

      {policy&&<ApiVersionPolicyCard productId={product.id} policy={policy}
                                     onSaved={async()=>{await reload();await onChanged();}}
                                     showToast={showToast}/>}

      {showCreate&&<NewApiVersionModal product={product} versions={versions}
                                       onCancel={()=>setShowCreate(false)} onCreate={create}/>}
      {deprecating&&(
        <DeprecateVersionModal
          row={deprecating}
          siblings={versions.filter(v=>v.id!==deprecating.id&&v.lifecycle!=='RETIRED')}
          onCancel={()=>setDeprecating(null)}
          onConfirm={async(body)=>{
            setDeprecating(null);
            await act(deprecating.id,'deprecate',body,deprecating.apiVersion+' deprecated');
          }}/>
      )}
    </div>
  );
}

/* ── Admin: create a version ───────────────────────────────────────────── */

function NewApiVersionModal({product,versions,onCancel,onCreate}){
  const highest=versions.length?versions[versions.length-1].apiVersion:'v1';
  const suggested='v'+((parseInt(String(highest).replace(/^v/,''),10)||1)+1);
  // Mirrors ProductVersionService.stripVersionSuffix. Without it the placeholder
  // promises "Jurist API v2 v4" while the server would actually create
  // "Jurist API v4" — a placeholder that lies about the default is worse than none.
  const stem=String(product.name||'API').replace(/\s+[vV]\d+$/,'').trim();
  const [form,setForm]=useState({apiVersion:suggested,name:'',version:'',migrationGuideUrl:''});
  const [saving,setSaving]=useState(false);
  const set=(k,v)=>setForm(f=>Object.assign({},f,{[k]:v}));

  const submit=async()=>{
    setSaving(true);
    const body={};
    Object.keys(form).forEach(k=>{if(String(form[k]||'').trim())body[k]=String(form[k]).trim();});
    await onCreate(body);
    setSaving(false);
  };

  return(
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal" style={{maxWidth:560}} onClick={e=>e.stopPropagation()}>
        <div className="modal-header"><div className="modal-title">New version of {product.name}</div></div>
        <div className="modal-body">
          <p style={{fontSize:12,color:'var(--text3)',marginTop:0}}>
            Copies this product's operations, quota, scopes and documentation into a new
            major version with its own subscribers. Subscriptions are <strong>not</strong>
            copied — everyone currently on {product.apiVersion||'this version'} stays there
            until they choose to move.
          </p>
          <div className="form-row" style={{display:'flex',gap:16,flexWrap:'wrap'}}>
            <div className="form-group" style={{flex:'0 0 120px'}}>
              <label className="form-label">Version token</label>
              <input className="form-input" value={form.apiVersion}
                     onChange={e=>set('apiVersion',e.target.value)}
                     style={{fontFamily:'monospace'}} placeholder={suggested}/>
            </div>
            <div className="form-group" style={{flex:'1 1 200px'}}>
              <label className="form-label">Name <span style={{color:'var(--text3)'}}>(optional)</span></label>
              <input className="form-input" value={form.name} onChange={e=>set('name',e.target.value)}
                     placeholder={stem+' '+form.apiVersion}/>
            </div>
            <div className="form-group" style={{flex:'0 0 120px'}}>
              <label className="form-label">Semver <span style={{color:'var(--text3)'}}>(optional)</span></label>
              <input className="form-input" value={form.version} onChange={e=>set('version',e.target.value)}
                     placeholder={String(form.apiVersion||'v2').replace(/^v/,'')+'.0.0'}/>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Migration guide URL <span style={{color:'var(--text3)'}}>(optional)</span></label>
            <input className="form-input" value={form.migrationGuideUrl}
                   onChange={e=>set('migrationGuideUrl',e.target.value)}
                   placeholder="https://axon.taxlab.ai/docs/…"/>
            <div style={{fontSize:11,color:'var(--text3)',marginTop:4}}>
              Published in the <code>Link</code> header of the version this one replaces,
              once you deprecate it.
            </div>
          </div>
          <p style={{fontSize:12,color:'var(--text3)',marginBottom:0}}>
            It is created as a <strong>draft</strong> and is not the default, so nothing is
            live until you publish it, sync it, and promote it.
          </p>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onCancel}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving||!form.apiVersion}>
            {saving?'Creating…':'Create version'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Admin: deprecate a version ────────────────────────────────────────── */

function DeprecateVersionModal({row,siblings,onCancel,onConfirm}){
  const defaultSuccessor=siblings.length?siblings[siblings.length-1].id:'';
  const [sunset,setSunset]=useState('');
  const [successor,setSuccessor]=useState(defaultSuccessor);
  const [guide,setGuide]=useState(row.migrationGuideUrl||'');

  const submit=()=>{
    const body={};
    // The API wants an ISO-8601 instant; a date input gives a plain day.
    if(sunset)body.sunsetAt=sunset+'T00:00:00Z';
    if(successor)body.supersededBy=successor;
    if(guide.trim())body.migrationGuideUrl=guide.trim();
    onConfirm(body);
  };

  return(
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal" style={{maxWidth:560}} onClick={e=>e.stopPropagation()}>
        <div className="modal-header"><div className="modal-title">Deprecate {row.apiVersion}</div></div>
        <div className="modal-body">
          <p style={{fontSize:12,color:'var(--text3)',marginTop:0}}>
            Nothing stops working. {row.apiVersion} stays fully served; every response starts
            carrying <code>Deprecation</code>, <code>Sunset</code> and a <code>Link</code> to
            its successor, so a customer finds out from their own logs rather than from an
            email they may not have read.
          </p>
          {row.activeSubscriptions>0&&(
            <div className="badge badge-warning" style={{display:'block',padding:'8px 10px',marginBottom:12}}>
              {row.activeSubscriptions} subscription{row.activeSubscriptions===1?'':'s'} still bound to this version.
            </div>
          )}
          <div className="form-row" style={{display:'flex',gap:16,flexWrap:'wrap'}}>
            <div className="form-group" style={{flex:'0 0 180px'}}>
              <label className="form-label">Sunset date</label>
              <input className="form-input" type="date" value={sunset} onChange={e=>setSunset(e.target.value)}/>
              <div style={{fontSize:11,color:'var(--text3)',marginTop:4}}>
                Advisory. Retiring is still a deliberate action.
              </div>
            </div>
            <div className="form-group" style={{flex:'1 1 220px'}}>
              <label className="form-label">Superseded by</label>
              <select className="form-input" value={successor} onChange={e=>setSuccessor(e.target.value)}>
                <option value="">— none —</option>
                {siblings.map(s=>(
                  <option key={s.id} value={s.id}>{s.apiVersion} — {s.name}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Migration guide URL</label>
            <input className="form-input" value={guide} onChange={e=>setGuide(e.target.value)}
                   placeholder="https://axon.taxlab.ai/docs/…"/>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onCancel}>Cancel</button>
          <button className="btn btn-primary" onClick={submit}>Deprecate and push routes</button>
        </div>
      </div>
    </div>
  );
}

/* ── Admin: how the family negotiates versions ─────────────────────────── */

function ApiVersionPolicyCard({productId,policy,onSaved,showToast}){
  const [form,setForm]=useState(Object.assign({
    strategy:'URI_PATH',headerName:'X-API-Version',queryParam:'api-version',acceptVendor:'taxlab',
    allowUnversioned:true,emitVersionHeader:true,emitDeprecationHeaders:true,tombstoneRetired:true
  },policy||{}));
  const [saving,setSaving]=useState(false);
  const set=(k,v)=>setForm(f=>Object.assign({},f,{[k]:v}));

  const save=async()=>{
    setSaving(true);
    try{
      await API('PUT','/api/admin/api-products/'+productId+'/version-policy',form);
      showToast&&showToast('Policy saved for every version — sync each one to apply it','success');
      await onSaved();
    }catch(e){showToast&&showToast(e.message,'error');}
    finally{setSaving(false);}
  };

  return(
    <div className="card">
      <div className="card-body">
        <div style={{fontWeight:600,marginBottom:4}}>Version negotiation</div>
        <p style={{fontSize:12,color:'var(--text3)',marginTop:0,maxWidth:640}}>
          Applies to every version in the family — a family where one version is selected by
          the path and another by a header cannot be described to a customer. Routes are not
          rewritten until you sync each version.
        </p>

        <div className="form-group" style={{maxWidth:460}}>
          <label className="form-label">How a caller selects a version</label>
          <select className="form-input" value={form.strategy} onChange={e=>set('strategy',e.target.value)}>
            {VERSION_STRATEGY_OPTIONS.map(([id,label])=><option key={id} value={id}>{label}</option>)}
          </select>
        </div>

        {form.strategy==='HEADER'&&(
          <div className="form-group" style={{maxWidth:260}}>
            <label className="form-label">Header name</label>
            <input className="form-input" value={form.headerName} onChange={e=>set('headerName',e.target.value)}
                   style={{fontFamily:'monospace'}}/>
          </div>
        )}
        {form.strategy==='QUERY_PARAM'&&(
          <div className="form-group" style={{maxWidth:260}}>
            <label className="form-label">Parameter name</label>
            <input className="form-input" value={form.queryParam} onChange={e=>set('queryParam',e.target.value)}
                   style={{fontFamily:'monospace'}}/>
          </div>
        )}
        {form.strategy==='ACCEPT_HEADER'&&(
          <div className="form-group" style={{maxWidth:320}}>
            <label className="form-label">Vendor token</label>
            <input className="form-input" value={form.acceptVendor} onChange={e=>set('acceptVendor',e.target.value)}
                   style={{fontFamily:'monospace'}}/>
            <div style={{fontSize:11,color:'var(--text3)',marginTop:4,fontFamily:'monospace'}}>
              application/vnd.{form.acceptVendor||'taxlab'}.v2+json
            </div>
          </div>
        )}

        <label style={{display:'flex',gap:8,alignItems:'flex-start',fontSize:13,marginTop:12}}>
          <input type="checkbox" checked={!!form.allowUnversioned} style={{marginTop:3}}
                 onChange={e=>set('allowUnversioned',e.target.checked)}/>
          <span>
            Keep serving unversioned requests
            <div style={{fontSize:11,color:'var(--text3)'}}>
              The default version also answers the bare paths, so every integration built
              before versioning existed keeps working untouched. Turning this off forces
              every caller to pin a version — and breaks the ones that have not.
            </div>
          </span>
        </label>

        <label style={{display:'flex',gap:8,alignItems:'flex-start',fontSize:13,marginTop:10}}>
          <input type="checkbox" checked={!!form.emitVersionHeader} style={{marginTop:3}}
                 onChange={e=>set('emitVersionHeader',e.target.checked)}/>
          <span>
            Return <code>X-API-Version</code> on every response
            <div style={{fontSize:11,color:'var(--text3)'}}>
              The only way a caller who never named a version can tell which one served them.
            </div>
          </span>
        </label>

        <label style={{display:'flex',gap:8,alignItems:'flex-start',fontSize:13,marginTop:10}}>
          <input type="checkbox" checked={!!form.emitDeprecationHeaders} style={{marginTop:3}}
                 onChange={e=>set('emitDeprecationHeaders',e.target.checked)}/>
          <span>
            Return <code>Deprecation</code>, <code>Sunset</code> and <code>Link</code> on a deprecated version
            <div style={{fontSize:11,color:'var(--text3)'}}>RFC 9745 and RFC 8594.</div>
          </span>
        </label>

        <label style={{display:'flex',gap:8,alignItems:'flex-start',fontSize:13,marginTop:10}}>
          <input type="checkbox" checked={!!form.tombstoneRetired} style={{marginTop:3}}
                 onChange={e=>set('tombstoneRetired',e.target.checked)}/>
          <span>
            Answer a retired version with <code>410 Gone</code>
            <div style={{fontSize:11,color:'var(--text3)'}}>
              Rather than deleting the route and leaving callers a bare 404, which is
              indistinguishable from a typo.
            </div>
          </span>
        </label>

        <div style={{marginTop:16}}>
          <button className="btn btn-secondary btn-sm" onClick={save} disabled={saving}>
            {saving?'Saving…':'Save policy'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Portal: what a customer sees ──────────────────────────────────────── */

/**
 * The banner above a deprecated version's docs.
 *
 * Deliberately not dismissible and deliberately at the top: the customer reading
 * this page is the person who has to act on it, and the date is the only part of
 * the message that matters.
 */
function PortalVersionBanner({product}){
  if(!product)return null;
  const deprecated=product.lifecycle==='DEPRECATED'||!!product.deprecatedAt;
  if(!deprecated)return null;

  const days=versionDaysToSunset(product.sunsetAt);
  const tone=versionSunsetTone(product.sunsetAt)||'warning';
  const newer=(product.otherVersions||[]).filter(v=>v.lifecycle==='PUBLISHED');
  const successor=newer.length?newer[newer.length-1]:null;

  return(
    <div className="card" style={{marginBottom:16,borderColor:'var(--'+tone+',var(--border))'}}>
      <div className="card-body">
        <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
          <span className={'badge badge-'+tone} style={{fontSize:10}}>Deprecated</span>
          <strong style={{fontSize:14}}>
            {product.apiVersion||'This version'} is being retired
          </strong>
        </div>
        <p style={{fontSize:13,color:'var(--text2)',margin:'8px 0 0'}}>
          {product.sunsetAt
            ?(days!==null&&days>=0
                ?'It stops being served on '+versionDay(product.sunsetAt)+' — '+days+' day'
                  +(days===1?'':'s')+' from now.'
                :'Its sunset date ('+versionDay(product.sunsetAt)+') has passed. It may stop being served at any time.')
            :'It is still fully served, but no sunset date has been set yet.'}
          {successor&&' Move to '+successor.apiVersion+' at '}
          {successor&&<code>{successor.basePath}</code>}
          {successor&&'.'}
        </p>
        {product.migrationGuideUrl&&(
          <a className="btn btn-sm btn-primary" style={{marginTop:12,display:'inline-block'}}
             href={product.migrationGuideUrl} target="_blank" rel="noopener noreferrer">
            Read the migration guide
          </a>
        )}
      </div>
    </div>
  );
}

/**
 * The version switcher in the portal sidebar. Renders nothing for an API with
 * only one version, which is every API until someone publishes a second.
 */
function PortalVersionSwitcher({product,onOpenVersion}){
  const others=(product&&product.otherVersions)||[];
  if(!others.length)return null;
  return(
    <div className="card" style={{marginTop:16}}>
      <div className="card-header"><div className="card-title">Other versions</div></div>
      <div className="card-body">
        <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:10,fontSize:13}}>
          <span style={{fontFamily:'monospace',fontWeight:600}}>{product.apiVersion}</span>
          <span className="badge badge-info" style={{fontSize:9}}>you are here</span>
          {product.isDefaultVersion&&<span className="badge badge-gray" style={{fontSize:9}}>default</span>}
        </div>
        {others.map(v=>(
          <div key={v.slug} style={{display:'flex',alignItems:'center',justifyContent:'space-between',
                                    gap:8,padding:'8px 0',borderTop:'1px solid var(--border)'}}>
            <div style={{minWidth:0}}>
              <div style={{display:'flex',alignItems:'center',gap:6}}>
                <span style={{fontFamily:'monospace',fontWeight:600,fontSize:13}}>{v.apiVersion}</span>
                <span className={'badge badge-'+(VERSION_LIFECYCLE_BADGE[v.lifecycle]||'gray')}
                      style={{fontSize:9}}>{v.lifecycle}</span>
              </div>
              <div style={{fontFamily:'monospace',fontSize:10,color:'var(--text3)'}}>{v.basePath}</div>
              {v.sunsetAt&&(
                <div style={{fontSize:10,color:'var(--text3)'}}>sunset {versionDay(v.sunsetAt)}</div>
              )}
            </div>
            {onOpenVersion&&(
              <button className="btn btn-xs btn-secondary" onClick={()=>onOpenVersion(v)}>Open</button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * How to pin a version — the difference between the two base paths, which is the
 * thing a customer most needs to understand before writing an integration.
 */
function PortalVersionPinCard({product}){
  if(!product||!product.gatewayBasePath)return null;
  const v=product.versioning||{};
  const token=product.apiVersion||'v1';
  return(
    <div className="card" style={{marginTop:16}}>
      <div className="card-header"><div className="card-title">Base path</div></div>
      <div className="card-body" style={{fontSize:12}}>
        <div style={{color:'var(--text3)'}}>Pinned to {token}</div>
        <div style={{fontFamily:'monospace',fontSize:12,marginBottom:10,wordBreak:'break-all'}}>
          {product.gatewayBasePath}
        </div>
        {v.strategy&&v.strategy!=='URI_PATH'&&v.strategy!=='NONE'&&(
          <div style={{marginBottom:10}}>
            <div style={{color:'var(--text3)'}}>Or select it with</div>
            <div style={{fontFamily:'monospace',fontSize:11,wordBreak:'break-all'}}>
              {v.strategy==='HEADER'&&(v.headerName||'X-API-Version')+': '+token}
              {v.strategy==='QUERY_PARAM'&&'?'+(v.queryParam||'api-version')+'='+token}
              {v.strategy==='ACCEPT_HEADER'&&'Accept: '+(v.acceptMediaType||'')}
            </div>
          </div>
        )}
        {product.unversionedBasePath&&(
          <React.Fragment>
            <div style={{color:'var(--text3)'}}>Unversioned</div>
            <div style={{fontFamily:'monospace',fontSize:12,wordBreak:'break-all'}}>
              {product.unversionedBasePath}
            </div>
            <p style={{color:'var(--text3)',marginBottom:0,marginTop:6}}>
              Follows whichever version is current. Fine for exploring; build your
              integration against the pinned path so a future release cannot move under you.
            </p>
          </React.Fragment>
        )}
      </div>
    </div>
  );
}

/** The version chip on a portal catalogue card. */
function PortalVersionChip({product}){
  if(!product||!product.apiVersion)return null;
  const tone=versionSunsetTone(product.sunsetAt);
  return(
    <React.Fragment>
      <span className="badge badge-gray" style={{fontSize:10,fontFamily:'monospace',flex:'none'}}>
        {product.apiVersion}
      </span>
      {product.sunsetAt&&(
        <span className={'badge badge-'+(tone||'warning')} style={{fontSize:10,flex:'none'}}>
          sunset {versionDay(product.sunsetAt)}
        </span>
      )}
    </React.Fragment>
  );
}
